import type { Context as AuthContext } from "@sal/authentication.react";

export interface RefreshResponse {
	success?: boolean;
	contactDetails?: Record<string, unknown>;
}

/**
 * CV2's mid-session token refresh: `POST /PortalFramework/Account/Refresh` re-issues `SWA-A`, then
 * re-syncs account-scoped permissions from `api/Accounts/ActivePermissions` — the Refresh response
 * itself only ever carries *base-contact* permissions (it's a SALCore4 endpoint with no awareness of
 * `X-Cv2-Account`), which would otherwise silently wipe whatever account-scoped permissions were
 * previously in `auth.user`. See docs/architecture/auth-and-account-permissions.md.
 *
 * Shared by two callers that reach a 401 by different paths:
 *  - `components/SessionExpiryProvider.tsx` registers this as `@sal/webrequest-web`'s
 *    `onRefreshToken` handler, invoked automatically whenever a `sandfieldFetch`-based request 401s.
 *  - `components/AccountProvider.tsx` uses raw `fetch()` for its bootstrap calls (it needs the raw
 *    `Response` for the 403/`X-Cv2-Access: not-enabled` gate and can't route through
 *    `@sal/webrequest-web`'s wrapper — see its own comments), so it detects a 401 itself and calls
 *    this directly to recover before retrying.
 *
 * Returns `true` if the session is confirmed valid afterwards (the refresh itself succeeded,
 * whether or not a company account's permissions came back — a base-contact-only result is still a
 * live session, just one with nothing account-scoped to sync yet). Returns `false` only when the
 * refresh itself failed, i.e. both `SWA-A` and `SWA-R` are gone and the session is genuinely dead.
 */
export async function refreshCv2Session(
	auth: Pick<AuthContext, "user" | "storeUserDetails">
): Promise<boolean> {
	let refreshJson: RefreshResponse | undefined;
	try {
		const response = await fetch("/PortalFramework/Account/Refresh", { method: "POST" });
		if (!response.ok) {
			return false;
		}
		refreshJson = (await response.json()) as RefreshResponse;
		if (!refreshJson?.success) {
			return false;
		}
	} catch {
		return false;
	}

	// Refresh succeeded — SWA-A has been re-issued. Its own response only carries base-contact
	// permissions, so re-sync the account-scoped ones.
	try {
		const permsRes = await fetch("api/Accounts/ActivePermissions");
		if (permsRes.ok) {
			const perms = await permsRes.json();
			auth.storeUserDetails({
				...auth.user,
				contactID: perms.contactId,
				permissions: perms.permissions
			});
			return true;
		}
	} catch {
		// Falls through to the base-contact fallback below, same as a non-OK response.
	}

	// No active company account, or a pending one: ActivePermissions legitimately has nothing to
	// return (403). Base-contact permissions are *correct* here, not a sign the session expired —
	// store whatever the refresh itself returned.
	if (refreshJson.contactDetails) {
		auth.storeUserDetails(refreshJson.contactDetails);
	}
	return true;
}
